/**
 * Coffee Slider Component
 * This script helps customers determine how much coffee they need based on daily consumption.
 */

if (!customElements.get("coffee-slider")) {
  class CoffeeSlider extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      // Number words mapping
      this.numberMap = {
        one: 1,
        two: 2,
        three: 3,
        four: 4,
        five: 5,
        six: 6,
        seven: 7,
        eight: 8,
        nine: 9,
        ten: 10,
        eleven: 11,
      };

      // Coffee data mapping
      this.data = {
        "two-weeks": {
          1: "One 10-12oz bag",
          2: "One 10-12oz bag",
          3: "Two 10-12oz bags",
          4: "One 28-32oz bag",
          5: "One 28-32oz bag",
          6: "Three 10-12oz bags",
          7: "Four 10-12oz bags",
          8: "Two 28-32oz bags",
          9: "One 4lb bag",
          10: "One 4lb bag",
          11: "One 4lb bag",
          12: "Three 28-32oz bags",
          13: "Three 28-32oz bags",
          14: "Three 28-32oz bags",
          15: "Three 28-32oz bags",
          16: "Two 4lb bags",
        },
        "one-month": {
          1: "One 10-12oz bag",
          2: "One 28-32oz bag",
          3: "Two 28-32oz bags",
          4: "One 4lb bag",
          5: "One 4lb bag",
          6: "Three 28-32oz bags",
          7: "Three 28-32oz bags",
          8: "Two 4lb bags",
          9: "Two 4lb bags",
          10: "Two 4lb bags",
          11: "Five 28-32oz bags",
          12: "Five 28-32oz bags",
          13: "Three 4lb bags",
          14: "Three 4lb bags",
          15: "Four 4lb bags",
          16: "Four 4lb bags",
        },
        "three-months": {
          1: "Four 10-12oz bags",
          2: "Three 28-32oz bags",
          3: "Two 4lb bags",
          4: "Three 4lb bags",
          5: "Four 4lb bags",
          6: "Four 4lb bags",
          7: "Five 4lb bags",
          8: "Six 4lb bags",
          9: "Six 4lb bags",
          10: "Seven 4lb bags",
          11: "Eight 4lb bags",
          12: "Eight 4lb bags",
          13: "Nine 4lb bags",
          14: "Ten 4lb bags",
          15: "Ten 4lb bags",
          16: "Eleven 4lb bags",
        },
      };

      // Get DOM elements
      this.input = this.querySelector("#coffee-slider__input");
      this.buttons = this.querySelectorAll(
        ".coffee-slider__frequencies button"
      );
      this.label = this.querySelector(".coffee-slider__label h4 span");
      this.results = this.querySelector(".coffee-slider__results p");
      this.badge = this.querySelector(".coffee-slider__results .badge");
      this.resultFrequency = this.querySelector(
        ".coffee-slider__result-frequency"
      );
      this.productButton = this.querySelector(".coffee-slider__product-button");
      this.shopNowButton = this.querySelector(".coffee-slider__shop-now-button");
      this.statusRegion = this.querySelector(".coffee-slider__status");

      if (!this.input) return;

      // Set initial size class
      this.classList.add("size-small");

      // Set initial frequency
      if (!this.classList.contains("frequency-two-weeks")) {
        this.classList.add("frequency-two-weeks");
      }

      this.setupEventListeners();

      // Initialize the slider with default values
      this.handleRangeInput({ announce: false });
    }

    // Convert text to number (e.g., "Two" to "x2")
    convertTextToNumber(text) {
      const words = text.toLowerCase().split(" ");
      for (let i = 0; i < words.length; i++) {
        if (this.numberMap[words[i]] !== undefined) {
          return `x${this.numberMap[words[i]]}`;
        }
      }
      return "x1"; // Default to x1 if no number word is found
    }

    setupEventListeners() {
      // Range input slider change
      this.input.addEventListener("input", this.handleRangeInput.bind(this));

      // Frequency button clicks
      this.buttons.forEach((button) => {
        button.addEventListener("click", this.handleFrequencyClick.bind(this));
      });

      if (this.shopNowButton) {
        this.handleShopNowButtonClick = this.handleShopNowClick.bind(this);
        this.shopNowButton.addEventListener("click", this.handleShopNowButtonClick);
      }
    }

    handleRangeInput(options = {}) {
      let selected = this.input.value;
      let cupText = selected === "1" ? "Cup" : "Cups";
      this.input.setAttribute(
        "aria-valuetext",
        `${selected} ${cupText.toLowerCase()} per day`
      );

      // Find active frequency button
      const activeButton = this.querySelector(
        ".coffee-slider__frequencies button.is-active"
      );
      if (!activeButton) {
        // If no button is active, set the first one as active
        this.buttons[0].classList.add("is-active");
      }

      let frequency = this.querySelector(
        ".coffee-slider__frequencies button.is-active"
      ).dataset.frequency;
      let content = this.data[frequency][selected];

      // Change cups per day text
      if (this.label) {
        this.label.textContent = `${selected} ${cupText}`;
      }

      // Change results
      if (this.results) {
        this.results.textContent = `${content}`;
      }

      // Remove existing size classes
      this.classList.remove("size-small", "size-medium", "size-large");

      // Add appropriate size class
      let selectedSize = "small";
      if (content.indexOf("28-32oz") > -1) {
        this.classList.add("size-medium");
        selectedSize = "medium";
      } else if (content.indexOf("4lb") > -1) {
        this.classList.add("size-large");
        selectedSize = "large";
      } else {
        this.classList.add("size-small");
      }

      if (this.productButton) {
        const productUrl =
          this.productButton.dataset[`${selectedSize}ProductUrl`];
        this.productButton.href =
          this.productButton.dataset.customUrl ||
          productUrl ||
          this.productButton.dataset.fallbackUrl;
      }

      if (this.shopNowButton) {
        this.shopNowButton.dataset.productUrl =
          this.shopNowButton.dataset[`${selectedSize}ProductUrl`] || "";
        this.shopNowButton.dataset.productHandle =
          this.shopNowButton.dataset[`${selectedSize}ProductHandle`] || "";
      }

      // Set badge text
      if (this.badge) {
        let xText = this.convertTextToNumber(content);
        this.badge.textContent = xText;
      }

      if (this.resultFrequency) {
        this.resultFrequency.textContent =
          this.querySelector(".coffee-slider__frequencies button.is-active")
            .textContent.trim();
      }

      if (options.announce !== false) this.announceResult();
    }

    announceResult() {
      if (!this.statusRegion) return;
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        const intro = this.querySelector(".coffee-slider__result-intro");
        const suffix = this.querySelector(".coffee-slider__result-suffix");
        this.statusRegion.textContent = [
          intro && intro.textContent.trim(),
          this.results && this.results.textContent.trim(),
          suffix && suffix.textContent.replace(/\s+/g, " ").trim(),
        ]
          .filter(Boolean)
          .join(" ");
      }, 600);
    }

    handleFrequencyClick(event) {
      if (!event.target.matches("button")) return;

      let frequency = event.target.dataset.frequency;

      // Update active button
      this.buttons.forEach((button) => {
        button.classList.remove("is-active");
        button.setAttribute("aria-pressed", "false");
      });
      event.target.classList.add("is-active");
      event.target.setAttribute("aria-pressed", "true");

      // Update frequency class
      this.classList.remove(
        "frequency-two-weeks",
        "frequency-one-month",
        "frequency-three-months"
      );
      this.classList.add(`frequency-${frequency}`);

      // Trigger input update
      this.handleRangeInput();
    }

    handleShopNowClick() {
      const productUrl = this.shopNowButton.dataset.productUrl;
      if (!productUrl) {
        window.location.href = this.shopNowButton.dataset.fallbackUrl;
        return;
      }

      const modal = document.getElementById(this.shopNowButton.dataset.modalId);
      if (!modal || typeof modal.show !== "function") {
        window.location.href = productUrl;
        return;
      }

      modal.dataset.productUrl = productUrl;
      modal.dataset.productHandle = this.shopNowButton.dataset.productHandle;
      modal.productUrl = productUrl;
      modal.show(this.shopNowButton);
    }

    // Lifecycle callback when the element is connected to the DOM
    connectedCallback() {
      // Check if already initialized
      if (this.hasAttribute("initialized")) return;

      // Mark as initialized
      this.setAttribute("initialized", "");

      // If somehow init wasn't called in constructor
      if (!this.input) {
        this.init();
      }
    }

    // Lifecycle callback when the element is disconnected from the DOM
    disconnectedCallback() {
      // Clean up event listeners if needed
      if (this.input) {
        this.input.removeEventListener("input", this.handleRangeInput);
      }

      if (this.buttons) {
        this.buttons.forEach((button) => {
          button.removeEventListener("click", this.handleFrequencyClick);
        });
      }

      if (this.shopNowButton && this.handleShopNowButtonClick) {
        this.shopNowButton.removeEventListener(
          "click",
          this.handleShopNowButtonClick
        );
      }
    }
  }

  // Register the custom element
  customElements.define("coffee-slider", CoffeeSlider);

  // Initialize on page load and handle Shopify section events
  document.addEventListener("DOMContentLoaded", () => {
    // Function to initialize coffee sliders
    const initSliders = () => {
      // Initialize any section with id="CoffeeSlider"
      const coffeeSliderSection = document.getElementById("CoffeeSlider");
      if (
        coffeeSliderSection &&
        !(coffeeSliderSection instanceof CoffeeSlider)
      ) {
        Object.setPrototypeOf(coffeeSliderSection, CoffeeSlider.prototype);
        coffeeSliderSection.init();
        coffeeSliderSection.setAttribute("initialized", "");
      }

      // Also handle any sections with class="coffee-slider" but not yet initialized
      const sliderSections = document.querySelectorAll(
        ".coffee-slider:not([initialized])"
      );

      sliderSections.forEach((section) => {
        if (!(section instanceof CoffeeSlider)) {
          // Extend the existing element with CoffeeSlider prototype
          Object.setPrototypeOf(section, CoffeeSlider.prototype);

          // Now initialize it
          section.init();
          section.setAttribute("initialized", "");
        }
      });
    };

    // Run on DOMContentLoaded
    initSliders();

    // Also listen for Shopify section events
    document.addEventListener("shopify:section:load", initSliders);
    document.addEventListener("shopify:section:select", initSliders);
    document.addEventListener("shopify:section:reorder", initSliders);
    document.addEventListener("shopify:section:unload", function () {
      // This is needed to properly reinitialize when sections are moved/reordered
      setTimeout(initSliders, 0);
    });
  });
}

if (!customElements.get("product-tabs-modal")) {
  class ProductTabsModal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector(".product-tabs-modal__dialog");
      this.content = this.querySelector("[data-product-tabs-modal-content]");
      this.closeButton = this.querySelector("[data-product-tabs-modal-close]");

      this.closeButton.addEventListener("click", () => this.hide());
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
      this.addEventListener("keydown", (event) => {
        if (event.key === "Escape") this.hide();
      });
      this.content.addEventListener("change", (event) => {
        const variantSelect = event.target.closest(
          "variant-selects, variant-radios"
        );
        if (!variantSelect) return;
        setTimeout(() => {
          this.syncSelectedVariantMedia(variantSelect);
          this.updateMediaHeight();
        });
      });
      this.handleResize = () => this.updateMediaHeight();
      window.addEventListener("resize", this.handleResize);

      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      this.productUrl = this.dataset.productUrl;
      this.setButtonLoading(true);
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      this.renderLoadingState();

      if (this.fetchController) this.fetchController.abort();
      this.fetchController = new AbortController();

      fetch(this.productUrl, { signal: this.fetchController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product request failed: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const productElement = responseHTML.querySelector(
            'section[id^="MainProduct-"]'
          );
          if (!productElement) throw new Error("Main product row was not found.");

          this.prepareProductElement(productElement);
          this.setInnerHTML(this.content, productElement.innerHTML);
          this.insertAppstleSelector();
          this.movePriceAfterVariantControls();
          this.initializeProductMedia();
          this.observeProductInfo();
          this.finishOpening();
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          console.error(error);
          this.content.innerHTML = `
            <div class="product-tabs-modal__error">
              <p>Product options could not be loaded.</p>
              <a class="button button--primary" href="${this.productUrl}">View product</a>
            </div>
          `;
          this.finishOpening();
        });
    }

    hide() {
      if (!this.hasAttribute("open")) return;
      this.removeAttribute("open");
      this.setButtonLoading(false);
      if (this.fetchController) this.fetchController.abort();
      this.fetchController = null;
      if (this.mediaSlider) {
        this.mediaSlider.destroy(true, true);
        this.mediaSlider = null;
      }
      if (this.productInfoObserver) {
        this.productInfoObserver.disconnect();
        this.productInfoObserver = null;
      }
      this.renderLoadingState();

      if (typeof removeTrapFocus === "function") {
        removeTrapFocus(this.openedBy);
      } else if (this.openedBy) {
        this.openedBy.focus();
      }

      document.body.classList.remove("overflow-hidden");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
    }

    renderLoadingState() {
      this.content.innerHTML = `
        <span class="loading-overlay__spinner">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </span>
      `;
    }

    finishOpening() {
      this.setButtonLoading(false);
      if (!this.hasAttribute("open")) return;
      requestAnimationFrame(() => this.updateMediaHeight());
      if (typeof trapFocus === "function") {
        trapFocus(this, this.dialog);
      } else {
        this.dialog.focus();
      }
    }

    setButtonLoading(isLoading) {
      if (!this.openedBy) return;
      this.openedBy.toggleAttribute("aria-disabled", isLoading);
      this.openedBy.classList.toggle("loading", isLoading);
      const spinner = this.openedBy.querySelector(".loading-overlay__spinner");
      if (spinner) spinner.classList.toggle("hidden", !isLoading);
    }

    prepareProductElement(productElement) {
      const originalSectionId = productElement.dataset.section;
      const modalSectionId = `coffee-slider-${this.id}-${originalSectionId}`;
      productElement.innerHTML = productElement.innerHTML.replaceAll(
        originalSectionId,
        modalSectionId
      );

      productElement
        .querySelectorAll("variant-selects, variant-radios")
        .forEach((variantSelect) => {
          variantSelect.dataset.originalSection = originalSectionId;
          variantSelect.dataset.updateUrl = "false";
        });
      productElement
        .querySelectorAll(
          "pickup-availability, product-modal, .product-info-modal, .customer"
        )
        .forEach((element) => element.remove());
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll("script").forEach((oldScript) => {
        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attribute) => {
          newScript.setAttribute(attribute.name, attribute.value);
        });
        newScript.appendChild(document.createTextNode(oldScript.textContent));
        oldScript.replaceWith(newScript);
      });
    }

    insertAppstleSelector() {
      const form = this.content.querySelector("product-form form");
      const buttons = form && form.querySelector(".product-form__buttons");
      if (!form || !buttons || form.querySelector(".appstle_stand_alone_selector")) {
        return;
      }

      const selector = document.createElement("span");
      selector.className = "appstle_stand_alone_selector";
      selector.dataset.productHandle = this.dataset.productHandle;
      selector.style.display = "none";
      buttons.insertAdjacentElement("beforebegin", selector);
    }

    movePriceAfterVariantControls() {
      const price = this.content.querySelector('.price-wrapper[id^="price-"]');
      const controls = this.content.querySelectorAll(
        ".product-form__controls-group"
      );
      const lastControls = controls[controls.length - 1];
      if (!price || !lastControls) return;

      const anchor = lastControls.closest("fieldset") || lastControls;
      anchor.insertAdjacentElement("afterend", price);
    }

    initializeProductMedia() {
      if (typeof Swiper !== "function") return;
      const sliderElement = this.content.querySelector(".js-media-list");
      if (!sliderElement) return;

      const nextButton = sliderElement.querySelector(".swiper-btn--next");
      const previousButton = sliderElement.querySelector(".swiper-btn--prev");
      const pagination = sliderElement.querySelector(".swiper-pagination");
      const isRtl = document.documentElement.getAttribute("dir") === "rtl";

      this.mediaSlider = new Swiper(sliderElement, {
        slidesPerView: 1,
        autoHeight: true,
        navigation: {
          nextEl: isRtl ? previousButton : nextButton,
          prevEl: isRtl ? nextButton : previousButton,
        },
        pagination: {
          el: pagination,
          clickable: true,
          type: "bullets",
          renderBullet: (index, className) =>
            `<span class="${className}"><em>${index}</em><i></i><b></b></span>`,
        },
      });
    }

    observeProductInfo() {
      if (this.productInfoObserver) this.productInfoObserver.disconnect();
      const productInfo = this.content.querySelector(".product__info-wrapper");
      if (!productInfo || typeof ResizeObserver !== "function") return;

      this.productInfoObserver = new ResizeObserver(() =>
        this.updateMediaHeight()
      );
      this.productInfoObserver.observe(productInfo);
    }

    updateMediaHeight() {
      const mediaWrapper = this.content.querySelector(
        ".product__media-wrapper"
      );
      if (!mediaWrapper) return;

      if (!window.matchMedia("(min-width: 901px)").matches) {
        mediaWrapper.style.removeProperty("--product-tabs-media-height");
        return;
      }

      const buttonsRow = this.content.querySelector(
        ".product-form__buttons-row"
      );
      if (!buttonsRow) return;

      const mediaTop = mediaWrapper.getBoundingClientRect().top;
      const buttonsBottom = buttonsRow.getBoundingClientRect().bottom;
      const mediaHeight = Math.max(0, Math.round(buttonsBottom - mediaTop));

      if (mediaHeight > 0) {
        mediaWrapper.style.setProperty(
          "--product-tabs-media-height",
          `${mediaHeight}px`
        );
        if (this.mediaSlider) this.mediaSlider.update();
      }
    }

    syncSelectedVariantMedia(variantSelect) {
      const featuredMedia =
        variantSelect.currentVariant &&
        variantSelect.currentVariant.featured_media;
      if (!featuredMedia || !this.mediaSlider) return;

      const media = this.content.querySelector(
        `[data-media-id="${variantSelect.dataset.section}-${featuredMedia.id}"]`
      );
      if (!media) return;

      this.mediaSlider.update();
      const slideIndex = Array.from(this.mediaSlider.slides).indexOf(media);
      if (slideIndex >= 0) this.mediaSlider.slideTo(slideIndex);
    }
  }

  customElements.define("product-tabs-modal", ProductTabsModal);
}
